/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationUserSessionIssuersMove } from "../funcs/organizationUserSessionIssuersMove.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { UserSessionIssuer } from "../models/components/usersessionissuer.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  MoveOrganizationUserSessionIssuerRequest,
  MoveOrganizationUserSessionIssuerSecurity,
} from "../models/operations/moveorganizationusersessionissuer.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type MoveOrganizationUserSessionIssuerMutationVariables = {
  request: MoveOrganizationUserSessionIssuerRequest;
  security?: MoveOrganizationUserSessionIssuerSecurity | undefined;
  options?: RequestOptions;
};

export type MoveOrganizationUserSessionIssuerMutationData = UserSessionIssuer;

export type MoveOrganizationUserSessionIssuerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * moveIssuer organizationUserSessionIssuers
 *
 * @remarks
 * Re-scope a user_session_issuer in the caller's organization. Provide a project_id to make it project-specific, or omit it to make it organization-owned. Existing clients and sessions move with the issuer. Requires org:admin.
 */
export function useMoveOrganizationUserSessionIssuerMutation(
  options?: MutationHookOptions<
    MoveOrganizationUserSessionIssuerMutationData,
    MoveOrganizationUserSessionIssuerMutationError,
    MoveOrganizationUserSessionIssuerMutationVariables
  >,
): UseMutationResult<
  MoveOrganizationUserSessionIssuerMutationData,
  MoveOrganizationUserSessionIssuerMutationError,
  MoveOrganizationUserSessionIssuerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildMoveOrganizationUserSessionIssuerMutation(client, options),
    ...options,
  });
}

export function mutationKeyMoveOrganizationUserSessionIssuer(): MutationKey {
  return ["@gram/client", "organizationUserSessionIssuers", "move"];
}

export function buildMoveOrganizationUserSessionIssuerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: MoveOrganizationUserSessionIssuerMutationVariables,
  ) => Promise<MoveOrganizationUserSessionIssuerMutationData>;
} {
  return {
    mutationKey: mutationKeyMoveOrganizationUserSessionIssuer(),
    mutationFn: function moveOrganizationUserSessionIssuerMutationFn({
      request,
      security,
      options,
    }): Promise<MoveOrganizationUserSessionIssuerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationUserSessionIssuersMove(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
